/**
 * 지출결의등록2 — 사업 기준 · 전자증빙→과목 매핑 유틸
 * 기존 결의등록(축 먼저)과 반대: 증빙 → 사업 → 편성과목 → 과목별 결의 일괄 생성
 */

import { loadExpenseBalanceRows } from 'expense/expenseLoadUtils';
import {
  loadCardAxisMaps,
  normalizeCardNo,
  detailFromEvidence,
  emptyCardAxisMap,
} from 'expense/expenseEvidenceStore';
import {
  createResolutionUid,
  createResolutionDetailUid,
  emptyResolutionMaster,
  expenseStatusLabel,
  EXPENSE_STATUS,
  todayYmd,
} from 'expense/expenseStore';

/** 현액이 없을 때 PoC용 — 동일 사업에 여러 과목 매핑 시나리오 */
export const POC_PROJECT_SUBJECT_FALLBACKS = [
  { subjectCode: '51101', subjectName: '일반운영비(수용비)' },
  { subjectCode: '51102', subjectName: '사무용품비' },
  { subjectCode: '51103', subjectName: '회의비' },
  { subjectCode: '51104', subjectName: '여비' },
];

/**
 * 증빙·카드축·현액에서 선택 가능한 사업 목록
 * @returns {{ projectCode: string, projectName: string, deptCode: string, deptName: string, fundCode: string, fundName: string, source: string }[]}
 */
export function listProjectsForResolutionByProject(tenantCode, fiscalYear, evidences = []) {
  const byCode = new Map();

  const add = (row, source) => {
    const code = String(row?.projectCode || '').trim();
    if (!code) return;
    if (byCode.has(code)) {
      const prev = byCode.get(code);
      if (!prev.projectName && row.projectName) prev.projectName = row.projectName;
      return;
    }
    byCode.set(code, {
      projectCode: code,
      projectName: String(row.projectName || '').trim(),
      deptCode: String(row.deptCode || '').trim(),
      deptName: String(row.deptName || '').trim(),
      fundCode: String(row.fundCode || '').trim(),
      fundName: String(row.fundName || '').trim(),
      source,
      id: code,
      code,
      name: String(row.projectName || '').trim() || code,
    });
  };

  const maps = loadCardAxisMaps(tenantCode) || [];
  const cardSet = new Set(
    (evidences || []).map((e) => normalizeCardNo(e.cardNo)).filter(Boolean)
  );
  maps.forEach((m) => {
    if (cardSet.size && !cardSet.has(normalizeCardNo(m.cardNo))) return;
    add(m, 'cardAxis');
  });
  // 카드 필터로 비었으면 전체 카드축 사업도 후보에 포함
  if (!byCode.size) {
    maps.forEach((m) => add(m, 'cardAxis'));
  }

  (loadExpenseBalanceRows(tenantCode, fiscalYear) || []).forEach((r) => add(r, 'balance'));

  return [...byCode.values()].sort((a, b) =>
    String(a.projectCode).localeCompare(String(b.projectCode), 'ko')
  );
}

/**
 * 카드축·현액에서 사업의 기본 부서·재원 추론
 */
export function resolveProjectAxisHint(tenantCode, fiscalYear, projectCode, evidences = []) {
  const code = String(projectCode || '').trim();
  if (!code) return emptyCardAxisMap();

  const maps = loadCardAxisMaps(tenantCode) || [];
  const cardSet = new Set(
    (evidences || []).map((e) => normalizeCardNo(e.cardNo)).filter(Boolean)
  );
  const hit =
    maps.find(
      (m) =>
        String(m.projectCode || '').trim() === code &&
        (!cardSet.size || cardSet.has(normalizeCardNo(m.cardNo)))
    ) || maps.find((m) => String(m.projectCode || '').trim() === code);
  if (hit) {
    return {
      ...emptyCardAxisMap(),
      ...hit,
      projectCode: code,
      projectName: hit.projectName || '',
    };
  }

  const bal = (loadExpenseBalanceRows(tenantCode, fiscalYear) || []).find(
    (r) => String(r.projectCode || '').trim() === code
  );
  if (bal) {
    return emptyCardAxisMap({
      projectCode: code,
      projectName: bal.projectName || '',
      deptCode: bal.deptCode || '',
      deptName: bal.deptName || '',
      fundCode: bal.fundCode || '',
      fundName: bal.fundName || '',
      subjectCode: bal.subjectDispCode || bal.subjectCode || '',
      subjectName: bal.subjectName || '',
      balanceUid: bal.balanceUid || '',
    });
  }

  return emptyCardAxisMap({ projectCode: code });
}

/**
 * 선택 사업에 편성(현액)된 과목 리스트
 * 현액 없으면 PoC 폴백 과목 + 카드축 과목
 */
export function listSubjectsForProject(tenantCode, fiscalYear, projectCode, axisHint = {}) {
  const code = String(projectCode || '').trim();
  if (!code) return [];

  const balances = (loadExpenseBalanceRows(tenantCode, fiscalYear) || []).filter(
    (r) => String(r.projectCode || '').trim() === code
  );

  if (balances.length) {
    return balances.map((r) => {
      const subjectCode = String(r.subjectDispCode || r.subjectCode || '').trim();
      return {
        ...r,
        id: r.balanceUid || `${r.deptCode}-${code}-${subjectCode}-${r.fundCode}`,
        subjectCode,
        subjectName: String(r.subjectName || '').trim(),
        availableAmt: Number(r.availableAmt ?? r.balanceAmt) || 0,
        balanceAmt: Number(r.balanceAmt) || 0,
        mapKey: subjectCode,
      };
    });
  }

  // 현액 없음 — 카드축 기본과목 + PoC 폴백
  const fallbacks = [];
  const seen = new Set();
  const push = (partial) => {
    const sc = String(partial.subjectCode || '').trim();
    if (!sc || seen.has(sc)) return;
    seen.add(sc);
    fallbacks.push({
      balanceUid: '',
      deptCode: axisHint.deptCode || '',
      deptName: axisHint.deptName || '',
      projectCode: code,
      projectName: axisHint.projectName || '',
      subjectCode: sc,
      subjectName: partial.subjectName || '',
      fundCode: axisHint.fundCode || '01',
      fundName: axisHint.fundName || '일반회계',
      availableAmt: Number(partial.availableAmt) || 9_999_999,
      balanceAmt: Number(partial.availableAmt) || 9_999_999,
      id: `poc-${code}-${sc}`,
      mapKey: sc,
      _pocFallback: true,
    });
  };

  if (axisHint.subjectCode) {
    push({
      subjectCode: axisHint.subjectCode,
      subjectName: axisHint.subjectName || '',
    });
  }
  POC_PROJECT_SUBJECT_FALLBACKS.forEach(push);
  return fallbacks;
}

/** 콤보 옵션: value=subjectCode */
export function subjectComboOptions(subjectRows = []) {
  return (subjectRows || []).map((r) => ({
    value: String(r.subjectCode || '').trim(),
    label: `${r.subjectCode} · ${r.subjectName || ''}`.trim(),
  }));
}

/**
 * 매핑 행(증빙+과목) → 과목별 그룹
 * @param {object[]} mappedRows mapSubjectCode 필수
 */
export function groupMappedEvidenceBySubject(mappedRows = []) {
  const groups = new Map();
  for (const row of mappedRows || []) {
    const sc = String(row.mapSubjectCode || row.subjectCode || '').trim();
    if (!sc) continue;
    if (!groups.has(sc)) groups.set(sc, []);
    groups.get(sc).push(row);
  }
  return groups;
}

/**
 * 과목 그룹 → 결의 마스터+디테일 초안 목록
 * @returns {{ masters: object[], details: object[], summary: { subjectCode, subjectName, count, amount, resolutionUid }[] }}
 */
export function buildResolutionsBySubjectGroups({
  fiscalYear,
  projectCode,
  projectName,
  subjectRows,
  axisHint,
  mappedRows,
}) {
  const subjectByCode = new Map(
    (subjectRows || []).map((s) => [String(s.subjectCode || '').trim(), s])
  );
  const groups = groupMappedEvidenceBySubject(mappedRows);
  const masters = [];
  const details = [];
  const summary = [];

  for (const [subjectCode, evRows] of groups.entries()) {
    const sub = subjectByCode.get(subjectCode) || {};
    const uid = createResolutionUid();
    const total = evRows.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const dates = evRows.map((e) => e.useDate).filter(Boolean).sort();
    const merchants = [...new Set(evRows.map((e) => e.merchantName).filter(Boolean))];
    const remark =
      merchants.length <= 2
        ? merchants.join(', ')
        : `${merchants[0]} 외 ${merchants.length - 1}건`;

    const master = emptyResolutionMaster({
      resolutionUid: uid,
      fiscalYear,
      resolutionDate: dates[dates.length - 1] || todayYmd(),
      registerDate: todayYmd(),
      status: EXPENSE_STATUS.DRAFT,
      resolutionAmt: total,
      remark: remark || `${subjectCode} 카드결의`,
      balanceUid: sub.balanceUid || '',
      deptCode: sub.deptCode || axisHint.deptCode || '',
      deptName: sub.deptName || axisHint.deptName || '',
      projectCode: projectCode || sub.projectCode || '',
      projectName: projectName || sub.projectName || axisHint.projectName || '',
      subjectCode,
      subjectName: sub.subjectName || evRows[0]?.mapSubjectName || '',
      fundCode: sub.fundCode || axisHint.fundCode || '',
      fundName: sub.fundName || axisHint.fundName || '',
      mod: 'A',
    });
    masters.push({
      ...master,
      statusNm: expenseStatusLabel(master.status),
    });

    evRows.forEach((e) => {
      details.push({
        ...detailFromEvidence(e, uid),
        detailUid: createResolutionDetailUid(),
        mod: 'A',
      });
    });

    summary.push({
      resolutionUid: uid,
      subjectCode,
      subjectName: master.subjectName,
      count: evRows.length,
      amount: total,
    });
  }

  return { masters, details, summary };
}

/**
 * 선택 증빙에서 단일 사업코드 추론 (카드축 기준)
 * @returns {{ projectCode: string, projectName: string } | null}
 */
export function inferSingleProjectFromEvidences(tenantCode, evidences = []) {
  const maps = loadCardAxisMaps(tenantCode) || [];
  const byCard = new Map(maps.map((m) => [normalizeCardNo(m.cardNo), m]));
  const codes = new Set();
  let name = '';
  for (const e of evidences || []) {
    const m = byCard.get(normalizeCardNo(e.cardNo));
    if (!m?.projectCode) continue;
    codes.add(String(m.projectCode).trim());
    name = m.projectName || name;
  }
  if (codes.size === 1) {
    const projectCode = [...codes][0];
    return { projectCode, projectName: name };
  }
  return null;
}
